/**
 * Admin Members Management Page
 * Two-section layout: Pending Approvals (top) and All Members (bottom)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import {
  fetchPendingMembers,
  fetchMembers,
  exportMembersXLSX,
} from '@/lib/api/members';
import { fetchPickupLocations } from '@/lib/api/pickupLocations';
import { MemberDetailDrawer, MemberDetailModal } from '@/components/admin/MemberDetailModal';
import { LocationColorBadge } from '@/components/ui/location-color-badge';
import { LocationColorDot } from '@/components/ui/location-color-dot';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Search,
  Download,
  X,
} from 'lucide-react';
import type { Member, PickupLocation, MemberFilters } from '@/types/api';

export function Members() {
  const { user } = useAuthStore();
  const { showToast } = useUIStore();
  const isDesktop = useMediaQuery('(min-width: 768px)');

  // State for pending members section
  const [pendingMembers, setPendingMembers] = useState<Member[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [isPendingSectionExpanded, setIsPendingSectionExpanded] = useState(true);
  const [loadingPending, setLoadingPending] = useState(true);

  // State for all members section
  const [members, setMembers] = useState<Member[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [exportingMembers, setExportingMembers] = useState(false);

  // Filters state
  const [filters, setFilters] = useState<MemberFilters>({
    status: 'ALL',
    role: undefined,
    pickupLocationId: undefined,
    search: '',
  });
  const [searchInput, setSearchInput] = useState('');

  // Modal state
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Pickup locations
  const [pickupLocations, setPickupLocations] = useState<PickupLocation[]>([]);

  const PAGE_SIZE = 20;

  // Load pickup locations on mount
  useEffect(() => {
    const loadPickupLocations = async () => {
      try {
        const locations = await fetchPickupLocations(false); // Get all locations
        setPickupLocations(locations);
      } catch (error) {
        showToast(
          error instanceof Error ? error.message : 'Failed to load pickup locations',
          'error'
        );
      }
    };

    loadPickupLocations();
  }, [showToast]);

  // Load pending members
  const loadPendingMembers = useCallback(async () => {
    try {
      setLoadingPending(true);
      const response = await fetchPendingMembers();
      setPendingMembers(response.members);
      setPendingCount(response.count);

      // Auto-expand if there are pending members
      if (response.count > 0) {
        setIsPendingSectionExpanded(true);
      }
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to load pending members',
        'error'
      );
    } finally {
      setLoadingPending(false);
    }
  }, [showToast]);

  // Load all members with filters
  const loadMembers = useCallback(async () => {
    try {
      setLoadingMembers(true);
      const response = await fetchMembers({
        ...filters,
        page,
        pageSize: PAGE_SIZE,
      });
      setMembers(response.members);
      setTotal(response.total);
      // If the list shrank (e.g. a member was deleted), don't strand the admin
      // on a now-empty page — the pagination controls hide when only one page
      // remains, leaving no way back.
      const newTotalPages = Math.max(1, Math.ceil(response.total / PAGE_SIZE));
      if (page > newTotalPages) {
        setPage(newTotalPages);
      }
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to load members',
        'error'
      );
    } finally {
      setLoadingMembers(false);
    }
  }, [filters, page, showToast]);

  // Load pending members only once on mount
  useEffect(() => {
    loadPendingMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load members whenever filters or page change
  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput }));
      setPage(1); // Reset to first page on search
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Handle filter changes
  const handleFilterChange = (key: keyof MemberFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page on filter change
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: 'ALL',
      role: undefined,
      pickupLocationId: undefined,
      search: '',
    });
    setSearchInput('');
    setPage(1);
  };

  const handleExportMembers = async () => {
    try {
      setExportingMembers(true);
      await exportMembersXLSX({
        ...filters,
        // Use the visible value so a click during the search debounce still
        // exports exactly what the user entered.
        search: searchInput,
      });
      showToast('Members exported successfully', 'success');
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to export members',
        'error'
      );
    } finally {
      setExportingMembers(false);
    }
  };

  // Open modal for approval mode
  const handlePendingMemberClick = (member: Member) => {
    setSelectedMember(member);
    setIsModalOpen(true);
  };

  // Open modal for edit mode
  const handleMemberClick = (member: Member) => {
    setSelectedMember(member);
    setIsModalOpen(true);
  };

  // Refresh both sections after approval/rejection/edit
  const handleModalSuccess = () => {
    loadPendingMembers();
    loadMembers();
  };

  const closeMemberDetails = () => {
    setIsModalOpen(false);
    setSelectedMember(null);
  };

  // Format date helper
  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Build a lookup map for O(1) pickup location access
  const locationById = useMemo(() => {
    const map = new Map<number, PickupLocation>();
    for (const loc of pickupLocations) map.set(loc.id, loc);
    return map;
  }, [pickupLocations]);

  // Check if user is admin
  if (user?.role !== 'ADMIN') {
    return (
      <div className="container mx-auto p-8 max-w-6xl">
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">
            You do not have permission to access this page.
          </p>
        </Card>
      </div>
    );
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const hasActiveFilters =
    filters.status !== 'ALL' ||
    filters.role !== undefined ||
    filters.pickupLocationId !== undefined ||
    filters.search !== '';

  return (
    <div className="container mx-auto p-8 max-w-7xl space-y-8">
      {/* SECTION 1: Pending Approvals */}
      <Card>
        <CardHeader
          className="cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => setIsPendingSectionExpanded(!isPendingSectionExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle>Pending Approvals</CardTitle>
              {pendingCount > 0 && (
                <Badge variant="default" className="bg-orange-500">
                  {pendingCount}
                </Badge>
              )}
            </div>
            <Button variant="ghost" size="icon-sm">
              {isPendingSectionExpanded ? (
                <ChevronUp className="h-5 w-5" />
              ) : (
                <ChevronDown className="h-5 w-5" />
              )}
            </Button>
          </div>
        </CardHeader>

        {isPendingSectionExpanded && (
          <CardContent>
            {loadingPending ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : pendingMembers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mb-3" />
                <p className="text-muted-foreground">No pending approvals</p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-semibold text-sm">
                          ITS ID
                        </th>
                        <th className="text-left py-3 px-4 font-semibold text-sm">
                          Name
                        </th>
                        <th className="text-left py-3 px-4 font-semibold text-sm">
                          Phone
                        </th>
                        <th className="text-left py-3 px-4 font-semibold text-sm">
                          Pickup Location
                        </th>
                        <th className="text-left py-3 px-4 font-semibold text-sm">
                          Registered Date
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingMembers.map((member) => {
                      const loc = member.pickupLocationId ? locationById.get(member.pickupLocationId) ?? null : null;
                      return (
                        <tr
                          key={member.id.toString()}
                          onClick={() => handlePendingMemberClick(member)}
                          className="border-b hover:bg-accent/50 cursor-pointer transition-colors"
                        >
                          <td className="py-3 px-4 text-sm">
                            {member.id.toString()}
                          </td>
                          <td className="py-3 px-4 text-sm font-medium">
                            {member.name}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {member.phone || 'N/A'}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            <LocationColorBadge colorCode={loc?.colorCode}>
                              {loc?.name || 'N/A'}
                            </LocationColorBadge>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {formatDate(member.createdAt)}
                          </td>
                        </tr>
                      );
                    })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-3">
                  {pendingMembers.map((member) => {
                    const loc = member.pickupLocationId ? locationById.get(member.pickupLocationId) ?? null : null;
                    return (
                      <Card
                        key={member.id.toString()}
                        className="p-4 cursor-pointer hover:bg-accent/50 transition-colors"
                        onClick={() => handlePendingMemberClick(member)}
                      >
                        <div className="space-y-2">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-semibold text-sm">{member.name}</p>
                              <p className="text-xs text-muted-foreground">
                                ITS ID: {member.id.toString()}
                              </p>
                            </div>
                            <Badge variant="default" className="bg-orange-500 text-xs">
                              Pending
                            </Badge>
                          </div>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Phone:</span>
                              <span>{member.phone || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Location:</span>
                              <LocationColorBadge colorCode={loc?.colorCode}>
                                {loc?.name || 'N/A'}
                              </LocationColorBadge>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Registered:</span>
                              <span>{formatDate(member.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        )}
      </Card>

      {/* SECTION 2: All Members */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>All Members</CardTitle>
            <Button
              onClick={handleExportMembers}
              disabled={exportingMembers || loadingMembers || total === 0}
              className="w-full sm:w-auto"
            >
              {exportingMembers ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Export XLSX
                </>
              )}
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Filter Bar */}
          <div className="flex flex-col gap-4 md:flex-row md:items-end">
            {/* Search */}
            <div className="flex-1 space-y-2">
              <label className="text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search ITS ID, name, or thaali number"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="w-full md:w-40 space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={filters.status || 'ALL'}
                onValueChange={(value) =>
                  handleFilterChange('status', value === 'ALL' ? 'ALL' : value)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Role Filter */}
            <div className="w-full md:w-40 space-y-2">
              <label className="text-sm font-medium">Role</label>
              <Select
                value={filters.role || 'ALL'}
                onValueChange={(value) =>
                  handleFilterChange('role', value === 'ALL' ? undefined : value)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  <SelectItem value="MEMBER">Member</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                  <SelectItem value="CATERER">Caterer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Pickup Location Filter */}
            <div className="w-full md:w-48 space-y-2">
              <label className="text-sm font-medium">Pickup Location</label>
              <Select
                value={filters.pickupLocationId?.toString() || 'ALL'}
                onValueChange={(value) =>
                  handleFilterChange(
                    'pickupLocationId',
                    value === 'ALL' ? undefined : parseInt(value)
                  )
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  {pickupLocations
                    .filter((loc) => loc.isActive)
                    .map((location) => (
                      <SelectItem key={location.id} value={location.id.toString()}>
                        <span className="flex items-center gap-2">
                          <LocationColorDot colorCode={location.colorCode} />
                          {location.name}
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="md:mb-0"
              >
                <X className="h-4 w-4 mr-2" />
                Clear
              </Button>
            )}
          </div>

          {/* Members Table */}
          {loadingMembers ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : members.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-muted-foreground">
                {hasActiveFilters
                  ? 'No members found matching your filters'
                  : 'No members found'}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold text-sm">
                        ITS ID
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-sm">
                        Name
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-sm">
                        Thaali #
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-sm">
                        Phone
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-sm">
                        Pickup Location
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-sm">
                        Status
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-sm">
                        Role
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-sm">
                        Registered
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => {
                      const loc = member.pickupLocationId ? locationById.get(member.pickupLocationId) ?? null : null;
                      return (
                      <tr
                        key={member.id.toString()}
                        onClick={() => handleMemberClick(member)}
                        className="border-b hover:bg-accent/50 cursor-pointer transition-colors"
                      >
                        <td className="py-3 px-4 text-sm">
                          {member.id.toString()}
                        </td>
                        <td className="py-3 px-4 text-sm font-medium">
                          {member.name}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {member.thaaliNumber || 'N/A'}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {member.phone || 'N/A'}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <LocationColorBadge colorCode={loc?.colorCode} className="font-medium">
                            {loc?.name || 'N/A'}
                          </LocationColorBadge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge
                            variant={
                              member.status === 'ACTIVE' ? 'success' : 'secondary'
                            }
                          >
                            {member.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm">{member.role}</td>
                        <td className="py-3 px-4 text-sm">
                          {formatDate(member.createdAt)}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {members.map((member) => {
                  const loc = member.pickupLocationId ? locationById.get(member.pickupLocationId) ?? null : null;
                  return (
                    <Card
                      key={member.id.toString()}
                      className="p-4 cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleMemberClick(member)}
                    >
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate">{member.name}</p>
                            <p className="text-xs text-muted-foreground">
                              ITS ID: {member.id.toString()}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <Badge
                              variant={
                                member.status === 'ACTIVE' ? 'success' : 'secondary'
                              }
                              className="text-xs"
                            >
                              {member.status}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {member.role}
                            </Badge>
                          </div>
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Thaali #:</span>
                            <span>{member.thaaliNumber || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Phone:</span>
                            <span>{member.phone || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Location:</span>
                            <LocationColorBadge colorCode={loc?.colorCode}>
                              {loc?.name || 'N/A'}
                            </LocationColorBadge>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Registered:</span>
                            <span>{formatDate(member.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t gap-3">
                  <p className="text-sm text-muted-foreground text-center sm:text-left">
                    Showing {(page - 1) * PAGE_SIZE + 1} to{' '}
                    {Math.min(page * PAGE_SIZE, total)} of {total} members
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <div className="flex items-center gap-2 px-3">
                      <span className="text-sm">
                        Page {page} of {totalPages}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Member Detail Modal */}
      {isDesktop && (
        <MemberDetailModal
          member={selectedMember}
          pickupLocations={pickupLocations}
          isOpen={isModalOpen}
          onClose={closeMemberDetails}
          onSuccess={handleModalSuccess}
        />
      )}
      {!isDesktop && (
        <MemberDetailDrawer
          member={selectedMember}
          pickupLocations={pickupLocations}
          isOpen={isModalOpen}
          onClose={closeMemberDetails}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
}
